/**
 * Status Badge Components for Project Detail Page
 * 
 * Reusable badge components for displaying various status indicators.
 */
import React from "react";
import { AlertTriangle, CheckCircle2, Check } from "lucide-react";

/**
 * Schedule Status Badge - Shows project schedule status
 */
export const ScheduleStatusBadge = ({ status, daysVariance }) => {
  let text, bgClass, textClass;
  
  if (status === 'behind') {
    text = `${Math.abs(daysVariance)} ${Math.abs(daysVariance) === 1 ? 'Day' : 'Days'} Behind`;
    bgClass = 'bg-amber-500/20';
    textClass = 'text-amber-400';
  } else if (status === 'ahead') {
    text = `${Math.abs(daysVariance)} ${Math.abs(daysVariance) === 1 ? 'Day' : 'Days'} Ahead`;
    bgClass = 'bg-emerald-500/20';
    textClass = 'text-emerald-400';
  } else if (status === 'complete') {
    text = 'Complete';
    bgClass = 'bg-emerald-500/20';
    textClass = 'text-emerald-400';
  } else {
    text = 'On Schedule';
    bgClass = 'bg-emerald-500/20';
    textClass = 'text-emerald-400';
  }
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${bgClass}`}>
      {status === 'behind' ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
      <span className={`text-sm font-bold uppercase tracking-wider ${textClass}`}>{text}</span>
    </div>
  );
};

/**
 * Milestone Status Badge - Shows individual milestone status
 */
export const MilestoneStatusBadge = ({ status, daysLate, progress }) => {
  if (status === 'complete') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400">
        <Check className="h-3 w-3" /> Complete
      </span>
    );
  }
  
  if (status === 'in_progress' && daysLate > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-red-500/20 text-red-400">
        {progress || 0}% • {daysLate}d late
      </span>
    );
  }
  
  if (status === 'in_progress') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold bg-blue-500/20 text-blue-400">
        {progress || 0}% In Progress
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-slate-700/50 text-slate-400">
      Not Started
    </span>
  );
};

/**
 * Payment Status Badge - Shows payment status
 */
export const PaymentStatusBadge = ({ status }) => {
  const config = {
    pending: { text: 'PENDING', bg: 'bg-slate-500/20', textColor: 'text-slate-400' },
    invoiced: { text: 'INVOICED', bg: 'bg-blue-500/20', textColor: 'text-blue-400' },
    paid: { text: 'PAID', bg: 'bg-emerald-500/20', textColor: 'text-emerald-400' },
    overdue: { text: 'OVERDUE', bg: 'bg-red-500/20', textColor: 'text-red-400' },
  };
  
  const c = config[status] || config.pending;
  
  return (
    <span className={`px-2 py-1 rounded text-xs font-bold uppercase tracking-wider ${c.bg} ${c.textColor}`}>
      {c.text || status}
    </span>
  );
};

/**
 * Metric Card - Reusable card component for displaying metrics
 */
export const MetricCard = ({ label, value, subtext, valueClass = "text-white" }) => (
  <div className="p-6 rounded-xl bg-slate-900/80">
    <span className="text-sm text-slate-500">{label}</span>
    <p className={`text-4xl font-bold mt-2 ${valueClass}`}>{value}</p>
    {subtext && <p className="text-sm text-slate-500 mt-1">{subtext}</p>}
  </div>
);
